import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import {
  ToastProvider, ThemeProvider, useResponsive, SessionCheck, SessionError,
} from '@parsina/framework';

import { KC_BASE_URL } from './src/lib/runtimeEnv.js';
import logo from 'url:./src/assets/logo.png';
import logoWhite from 'url:./src/assets/logo-white.png';
import { I18nProvider, useTranslation } from './src/i18n/I18nProvider.jsx';
import {
  getToken, getRefresh, isTokenExpired,
  handleCallback, redirectToLogin, refreshAccessToken,
  clearTokens, clearRedirectGuard,
  takeCallbackError, CALLBACK_ERROR_BAD_REDIRECT,
} from './src/lib/auth.js';
import { errorCode } from './src/lib/bffClient.js';
import { takeReturnPath, onSessionEvent, SessionEvent } from './src/lib/session.js';
import {
  ShortcutProvider, useShortcut, useShortcutHelp, closeTopOverlay,
} from './src/lib/shortcuts.js';
import { bootIdentity, rebootIdentity, isOnboarded } from './src/hooks/useMe.js';
import StudentLayout from './src/components/common/StudentLayout.jsx';
import MobileShell from './src/components/mobile/MobileShell.jsx';
import ShortcutHelp from './src/components/common/ShortcutHelp.jsx';
import SessionDialog from './src/components/common/SessionDialog.jsx';
import LoginScreen from './src/screens/Login/index.jsx';
import OnboardingScreen from './src/screens/Onboarding/index.jsx';
import PatientsScreen from './src/screens/Patients/index.jsx';
import PatientRecordScreen from './src/screens/PatientRecord/index.jsx';
import VisitScreen from './src/screens/Visit/index.jsx';
import ChatScreen from './src/screens/Chat/index.jsx';
import ChatLandingScreen from './src/screens/ChatLanding/index.jsx';
import CreditsScreen from './src/screens/Credits/index.jsx';
import ProfileScreen from './src/screens/Profile/index.jsx';
import { setupPWA } from './src/lib/registerSW.js';
import './styles.css';

// ── The boot ────────────────────────────────────────────────────────────────
//
// token → `onboardingStatus` → the form, or the app. core.md §4.2/§4.3.
//
// The boot query is `BOOT_QUERY` in src/graphql/documents.js — `onboardingStatus`
// + `studentProfile`, the two roots the BFF's gate ALWAYS allows — and it is
// issued through `bootIdentity()` in src/hooks/useMe.js rather than directly.
// That is not indirection for its own sake: the rail, the chat composer, the
// credits screen and the profile all read the same two facts, and a gate that
// fetched them on its own would leave every one of those to fetch them again.
//
// ── Onboarding is a BOOT DECISION, not a route guard ────────────────────
//
// `/onboarding` is rendered INSTEAD of the router when the status says the
// student has no profile yet. A route guard would be a redirect, and a redirect
// is a thing a half-typed form does not survive — while this way the session
// dialog (§15) can overlay the form without unmounting it.
//
// It also gives the "already onboarded opens /onboarding" case (core.md §4.3)
// for free: that student gets the ROUTER, whose catch-all lands them on
// `/patients`. The redirect is arrived at by not rendering a screen.
//
// ── A failed boot is an OUTAGE, never a verdict about the account ───────
//
// The doctor app once read a stale token as "this doctor has not registered",
// showed the registration wizard to someone already registered, and produced a
// duplicate account. The phase machine below has no shape that can repeat it:
// the ONLY thing that sends a student to the form is
// `onboardingStatus.isOnboarded === false`. Every failure is `bootError`, which
// offers a retry and says the student's patients are untouched.

/**
 * The app's own global bindings, registered ONCE.
 *
 * `?` opens the help overlay and `Esc` closes whatever sheet is on top; the
 * patient search's `Ctrl/⌘+K` is registered by the patients screen, so it binds
 * only while that screen is mounted — which is what core.md §7.0 means by "on
 * `/patients`" — and the visit workspace registers its own while it is open.
 *
 * Nothing anywhere else attaches a `window` keydown listener: two screens
 * binding the same key independently is invisible until they are mounted
 * together, and a key nobody can enumerate is a key the `?` overlay cannot list.
 */
const GlobalShortcuts = () => {
  const { t } = useTranslation();
  const help = useShortcutHelp();

  useShortcut('?', () => help.toggle(), {
    label: t('shortcuts.help'),
    group: t('shortcuts.groups.general'),
  });

  useShortcut('esc', () => {
    // The overlay bus, not a per-sheet listener: the topmost registered closer
    // wins, so a dialog over a sheet closes the dialog.
    if (help.open) {
      help.setOpen(false);
      return;
    }
    closeTopOverlay();
  }, {
    label: t('shortcuts.close'),
    group: t('shortcuts.groups.general'),
  });

  return null;
};

/** Restores the route a re-login came back from (core.md §15). One-shot. */
const ReturnTo = () => {
  const location = useLocation();
  const [target] = React.useState(() => takeReturnPath());
  if (!target || target === `${location.pathname}${location.search}`) return null;
  return <Navigate to={target} replace />;
};

const AppShell = () => {
  // ≤768px gets the mobile shell — bottom bar, gradient headers, one page
  // scroll — and not a 68px rail eating 19% of a 360px viewport. The ROUTES are
  // identical; only the chrome forks.
  const { isMobile } = useResponsive();
  const Shell = isMobile ? MobileShell : StudentLayout;

  return (
    <Shell>
      <ReturnTo />
      <Routes>
        {/* «بیماران» is the landing route: a student opens this app to their own
            practice patients, because there is nothing else it could open to —
            no day sheet, no queue, no dashboard (core.md §1.3). */}
        <Route path="/patients" element={<PatientsScreen />} />

        {/* The two FIXED-SEGMENT routes come first: `/patients/:caseId/:tab?`
            would otherwise swallow `/patients/x/visit/y` and render the record
            with `tab = "visit"`. */}
        <Route path="/patients/:caseId/visit/:encounterId" element={<VisitScreen />} />
        <Route path="/patients/:caseId/chat/:threadId?" element={<ChatScreen />} />
        <Route path="/patients/:caseId/:tab?" element={<PatientRecordScreen />} />

        <Route path="/chat" element={<ChatLandingScreen />} />
        <Route path="/credits" element={<CreditsScreen />} />
        <Route path="/profile" element={<ProfileScreen />} />

        {/* Ten routes, ten built screens. A route for scheduling, a clinic, a
            case library or a progress dashboard would be a route to nothing —
            core.md §18 says building any of them is a defect, and a route is the
            first thing that appears for a screen nobody has built. */}
        <Route path="/" element={<Navigate to="/patients" replace />} />
        <Route path="*" element={<Navigate to="/patients" replace />} />
      </Routes>
    </Shell>
  );
};

const AuthGate = () => {
  const { t } = useTranslation();
  // 'checking'    resolving tokens / the OIDC callback
  // 'login'       no usable session; the sign-in screen, not an instant redirect
  // 'booting'     authenticated, asking the BFF who this is
  // 'onboarding'  verified, but there is no profile yet
  // 'ready'       the app
  // 'bootError'   the boot query failed for a reason that is NOT auth
  // 'loop'        auth.js detected a redirect loop and bailed
  const [phase, setPhase] = React.useState('checking');
  const [attempt, setAttempt] = React.useState(0);
  // The one sentence the sign-in screen may carry beside its button: set when
  // Keycloak refused the redirect for THIS origin (`invalid_redirect_uri`),
  // where pressing the button again is exactly the loop to avoid.
  const [loginNotice, setLoginNotice] = React.useState('');

  /**
   * @param fresh  discard the cached identity first. The normal boot dedupes on
   *               the in-flight query and keeps a resolved one; a mid-session
   *               NOT_ONBOARDED means the cached answer is the thing that is
   *               wrong, so that path has to ask again.
   */
  const boot = React.useCallback(async (fresh = false) => {
    try {
      await (fresh ? rebootIdentity() : bootIdentity());
      setPhase(isOnboarded() ? 'ready' : 'onboarding');
    } catch (err) {
      const code = errorCode(err);
      if (code === 'UNAUTHENTICATED' || err?.status === 401) {
        // The session is gone. Back to the sign-in SCREEN rather than straight
        // to Keycloak: a student who lands here from a bookmark gets a page to
        // read, and the redirect-loop guard has a frame to report from.
        clearTokens();
        setPhase('login');
        return;
      }
      // Anything else is an OUTAGE, not a verdict about this account —
      // NOT_ONBOARDED included, which cannot reach here anyway: both roots in
      // the boot query are on the gate's allow-list.
      console.error('[boot] failed:', code || err?.message);
      setPhase('bootError');
    }
    // Intentionally NO dependencies: everything boot touches is a stable
    // setState or a module import. A dependency here re-arms the effect that
    // calls it — which is how a sibling client once fired ~141 GraphQL requests
    // before the page painted once.
  }, []);

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('auth_error') === 'loop') {
      setPhase('loop');
      return;
    }
    (async () => {
      // Returning from Keycloak: exchange the code first.
      if (params.has('code') || params.has('state') || params.has('error')) {
        if (await handleCallback()) {
          clearRedirectGuard();
          setPhase('booting');
          boot();
          return;
        }
        // `invalid_redirect_uri` is not "try again": this origin is not
        // registered on the client, and the sign-in screen has to SAY so
        // (core.md §7.1) instead of offering the button that produced it.
        if (takeCallbackError() === CALLBACK_ERROR_BAD_REDIRECT) {
          // The KEY, translated at render: `t` is not a dependency of this
          // effect, and must not become one (a locale switch would re-boot).
          setLoginNotice('login.badRedirect');
          setPhase('login');
          return;
        }
      }
      const token = getToken();
      if (token && !isTokenExpired(token)) {
        clearRedirectGuard();
        setPhase('booting');
        boot();
        return;
      }
      if (getRefresh() && (await refreshAccessToken())) {
        clearRedirectGuard();
        setPhase('booting');
        boot();
        return;
      }
      setPhase('login');
    })();
  }, [boot, attempt]);

  // ── NOT_ONBOARDED mid-session (core.md §4.4) ──────────────────────────
  //
  // Every root but the two boot roots answers `NOT_ONBOARDED` to a verified
  // token with no profile. It cannot happen at boot (both boot roots are
  // allowed), so if a screen sees it the profile this app booted on is GONE —
  // deleted from another tab, or the store restored from before it existed.
  // Rendering `errors.generic` on that screen leaves the student on a router
  // whose every request will fail the same way. The fetch wrapper raises it
  // on the session bus (a bus, because the wrapper must never navigate —
  // that is what would unmount the SOAP draft); this is the one subscriber,
  // and its answer is to ask `onboardingStatus` again from scratch and let the
  // gate re-decide between the router and the onboarding screen.
  React.useEffect(() => onSessionEvent((event) => {
    if (event !== SessionEvent.NOT_ONBOARDED) return;
    if (phase !== 'ready') return;
    setPhase('booting');
    boot(true);
  }), [phase, boot]);

  // The session screens are the kit's (packages/DESIGN.md §"Session check") —
  // the same card, motion and manual sign-in link as every other Parsina
  // panel. Only the words and the logo are this panel's.
  if (phase === 'loop') {
    return (
      <SessionError
        logo={logo}
        brandName={t('nav.brand.name')}
        title={t('auth.loopTitle')}
        body={t('auth.loopError')}
        actionLabel={t('auth.retry')}
        onAction={() => {
          clearTokens();
          clearRedirectGuard();
          window.location.replace('/');
        }}
        host={KC_BASE_URL.replace(/^https?:\/\//, '')}
      />
    );
  }

  if (phase === 'bootError') {
    return (
      <SessionError
        tone="warning"
        testId="boot-error"
        stateTestId="boot-error-state"
        logo={logo}
        brandName={t('nav.brand.name')}
        title={t('auth.bootFailed.title')}
        body={t('auth.bootFailed.body')}
        actionLabel={t('common.actions.retry')}
        onAction={() => { setPhase('checking'); setAttempt((n) => n + 1); }}
        host={KC_BASE_URL.replace(/^https?:\/\//, '')}
      />
    );
  }

  if (phase === 'checking' || phase === 'booting') {
    return (
      <SessionCheck
        logo={logoWhite}
        brandName={t('nav.brand.name')}
        panelName={t('nav.brand.panel')}
        title={phase === 'checking' ? t('auth.checking') : t('auth.loading')}
        subtitle={t('auth.checkingHint')}
        stuckLabel={t('auth.stuck')}
        manualLabel={t('auth.manualSignIn')}
        onManualSignIn={() => { redirectToLogin(); }}
      />
    );
  }

  if (phase === 'login') {
    // Outside the router on purpose: there is no app to navigate around yet.
    return <SignIn notice={loginNotice ? t(loginNotice) : ''} />;
  }

  // Outside the router too — see the header. The student has a verified token
  // and no profile; there is exactly one thing to do and no route to be at.
  if (phase === 'onboarding') {
    return <OnboardingScreen onDone={() => setPhase('ready')} />;
  }

  return (
    <BrowserRouter>
      <AppShell />
    </BrowserRouter>
  );
};

/** The sign-in screen and the one redirect it starts. */
const SignIn = ({ notice = '' }) => {
  const [busy, setBusy] = React.useState(false);
  return (
    <LoginScreen
      busy={busy}
      notice={notice}
      onSignIn={async () => {
        setBusy(true);
        // `redirectToLogin` navigates away, so nothing after it runs on success.
        // It returns without navigating only when the loop guard has tripped, in
        // which case it has already rewritten the URL and reloaded.
        await redirectToLogin();
        setBusy(false);
      }}
    />
  );
};

const DocumentTitle = () => {
  const { t, locale } = useTranslation();
  React.useEffect(() => {
    document.title = t('app.title');
  }, [t, locale]);
  return null;
};

// Provider order is load-bearing:
//   ThemeProvider outermost — it applies the cached accent in a lazy initialiser,
//     i.e. BEFORE first paint, so a reload does not flash the default accent.
//   I18nProvider above ToastProvider — the sign-in and auth-error screens read
//     t(), and so do the toasts.
//   ShortcutProvider inside I18nProvider — every binding is registered with an
//     already-translated label for the `?` overlay.
const App = () => (
  <ThemeProvider>
    <I18nProvider>
      <ToastProvider>
        <ShortcutProvider>
          <DocumentTitle />
          <GlobalShortcuts />
          <AuthGate />
          {/* Above the app, outside the router: a session ends wherever the
              student happens to be, and the dialog must not unmount them. */}
          <SessionDialog />
          <ShortcutHelp />
        </ShortcutProvider>
      </ToastProvider>
    </I18nProvider>
  </ThemeProvider>
);

ReactDOM.createRoot(document.getElementById('root')).render(<App />);

// After render, never awaited: an app that cannot install must still run.
setupPWA();
